import { useCallback } from 'react'
import { useOptionalActiveChatId } from '@/components/AppShell/AppShellLayoutContext'
import { registerArtifactPayload } from '@/features/documentPreview/artifactPayloadStore.js'
import { fetchChatArtifactAccess } from '@/services/chat/chatArtifactsApi'
import { previewKind, previewPayload } from './artifactCardHelpers'

/**
 * Returns a handler that opens the preview for an artifact card.
 *
 * The persisted `preview_url` is a sandbox inline token that expires
 * (currently 7 days). For long-lived cards we re-mint a fresh URL from
 * `/v2/artifacts/access` at click time so the panel never opens against
 * a 401. Office files (DOCX/PPTX/XLSX/CSV) reach the panel via a
 * durable `document_id` instead, which the API renders on demand.
 */
export function useArtifactCardPreview(block, { canPreview, preview, title, documentId }) {
  const chatId = useOptionalActiveChatId()
  return useCallback(async () => {
    if (!canPreview) return
    const kind = previewKind(block.kind)
    const description = `${String(block.kind ?? 'artifact').toUpperCase()}${block.version ? ` v${block.version}` : ''}`
    if (documentId) {
      const artifact = {
        artifactId: block.artifact_id,
        kind,
        fileName: title,
        description,
        payload: { documentId, highlights: [], coarseHighlights: [] },
      }
      registerArtifactPayload(artifact)
      preview.openPreview(artifact)
      return
    }
    let previewUrl = block.preview_url ?? null
    if (chatId && block.artifact_id) {
      try {
        const fresh = await fetchChatArtifactAccess(chatId, block.artifact_id, { inline: true })
        if (fresh) previewUrl = fresh
      } catch {
        // Renderer will surface its own error on the persisted URL.
      }
    }
    const artifact = {
      artifactId: block.artifact_id,
      kind,
      fileName: title,
      description,
      payload: previewPayload(kind, previewUrl),
    }
    registerArtifactPayload(artifact)
    preview.openPreview(artifact)
  }, [block, canPreview, preview, title, documentId, chatId])
}
